import { useInfiniteQuery } from "@tanstack/react-query";
import InvoicingTag from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/components/scrollable-calendar-view/components/appointment-timeslot/components/invoicing-tag/InvoicingTag";
import {
  TYPE_FILTER_OPTIONS,
  getItemsByTimeWithSuperBill,
} from "components/Dashboard/utility/getItemsByTime";
import Loading from "components/Loading";
import ActionsDropdown from "components/actions-dropdown/ActionsDropdown";
import AppPagination from "components/app-pagination";
import ConfirmModal from "components/confirm-modal";
import ErrorTag from "components/error-tag";
import { isInvoiceWrittenOff } from "components/invoicing/utils/invoiceStatus";
import LazyImage from "components/lazy-image";
import { useUser } from "contexts/UserContext";
import useFetchInvoicesForAppointments from "hooks/useFetchInvoicesForAppointments";
import useFetchSuperbillsForAppointments from "hooks/useFetchSuperbillsForAppointments";
import messages from "messages";
import moment from "moment-timezone";
import { appointmentsV2InfiniteQueryOptions } from "queries/appointmentsV2QueryOptions";
import useFetchAllPagesForInfiniteQuery from "queries/useFetchAllPagesForInfiniteQuery";
import { useMemo, useState } from "react";
import { Button, Dropdown, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import rejectSuperbill from "services/superbill/rejectSuperbill";
import { AmplitudePageName, track } from "utils/amplitude";
import { apiHostAndPath } from "utils/api";
import downloadFromDifferentOrigin from "utils/downloadFromDifferentOrigin";
import getClasses from "utils/getClasses";
import styles from "./BillingList.module.scss";

const SUPERBILL_EMPTY_STATE = "/img/billing/superbill-empty-state.png";
const RECEIPT_EMPTY_STATE = "/img/billing/receipt-empty-state.png";
const COUNT_PER_PAGE = 10;
const AMPLITUDE_COMPONENT_NAME = "billing-list";

interface Props {
  amplitudePageName?: AmplitudePageName;
  activeTab: string;
}

const BillingList: React.FC<Props> = ({ amplitudePageName, activeTab }) => {
  const { user, defaultUserTimezone } = useUser();

  const appointmentsQuery = useInfiniteQuery(
    appointmentsV2InfiniteQueryOptions({}),
  );
  useFetchAllPagesForInfiniteQuery(appointmentsQuery);

  const appointments = useMemo(
    () =>
      appointmentsQuery.data?.pages.flatMap((page) => page.appointments) ?? [],
    [appointmentsQuery.data?.pages],
  );

  const filteredByMinorOrGuardian = useMemo(() => {
    // check if user is a guardian
    if (!!user?.minors?.length) {
      if (!!user?.current_minor_user_id) {
        return appointments.filter(
          (el) => el?.patient?.orchid_user_id === user?.current_minor_user_id,
        );
      } else {
        return appointments.filter(
          (el) => el?.patient?.orchid_user_id === user?.sub,
        );
      }
    } else {
      return appointments;
    }
  }, [appointments, user]);

  const { appointmentsWithSuperBill, isLoadingSuperbills } =
    useFetchSuperbillsForAppointments(filteredByMinorOrGuardian || []);

  const { appointmentsWithInvoices, isLoadingInvoices } =
    useFetchInvoicesForAppointments(
      appointmentsWithSuperBill.filter((el) => el.statusFlags.usingInvoicing),
    );

  const filteredItems = useMemo(
    () => appointmentsWithInvoices.filter((el) => el.invoice),
    [appointmentsWithInvoices],
  );

  const isLoading =
    appointmentsQuery.isPending ||
    appointmentsQuery.isLoading ||
    appointmentsQuery.hasNextPage ||
    isLoadingInvoices ||
    isLoadingSuperbills;

  // page start
  const [pageSuperbill, setPageSuperbill] = useState(0);
  const [pageReceipt, setPageReceipt] = useState(0);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [statementNoToDelete, setStatementNoToDelete] = useState("");

  const [error, setError] = useState<any>();

  const items = useMemo(() => {
    return getItemsByTimeWithSuperBill(
      appointmentsWithSuperBill,
      [],
      +new Date(),
    );
  }, [appointmentsWithSuperBill]);

  const superbills = useMemo(() => {
    return items.filter(
      (el) => el.superbills[0]?.generate_state === "superbill_finalized",
    );
  }, [items]);

  const receipts = useMemo(() => {
    return items.filter((el) => el.invoiceAvailable === "available");
  }, [items]);

  const onDeleteSuperbill = async (statementNo) => {
    try {
      await rejectSuperbill(statementNo);
      window.alert("delete successfully");
    } catch (error: any) {
      setError(error?.message || error.toString());
    }
    setShowConfirmDeleteModal(false);
  };

  return (
    <>
      {error && (
        <ErrorTag
          show={true}
          onHide={() => setError(undefined)}
          duration={5000}
          errorMsg={error?.toString()}
        />
      )}

      {activeTab === "invoices" &&
        user?.user_type !== "orchid_pro" &&
        (filteredItems.length === 0 ? (
          <div className={styles["billing-list-empty-state"]}>
            <LazyImage src={RECEIPT_EMPTY_STATE} width={168} height={"auto"} />
            <div className={styles["billing-list-empty-state__title"]}>
              No Invoices.
            </div>
            <div className={styles["billing-list-empty-state__sub-title"]}>
              Your invoice list will appear here.
            </div>
          </div>
        ) : (
          <div className={getClasses(styles["billing-list"])}>
            <Table className={styles["billing-list__table"]}>
              <thead>
                <tr>
                  <th>Date Issued</th>
                  <th>Invoice #</th>
                  <th>Status</th>
                  <th>Session Type</th>
                  <th>Payer</th>
                  <th className={"text-right"}>Session Fee</th>
                  <th className={"text-right"}>Payment Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(({ invoice }, i) => {
                  return (
                    <tr key={`${i}-${invoice.appointment_id}`}>
                      <td>
                        {moment
                          .tz(invoice.invoice_issued_at, defaultUserTimezone)
                          .format("MM/DD/YYYY z")}
                      </td>
                      <td>{`#${invoice.invoice_no}`}</td>
                      <td>
                        <InvoicingTag invoice={invoice} />
                      </td>
                      <td>
                        {appointmentsWithInvoices[i].appointment_type_label}
                      </td>
                      <td className={styles["billing-list__table-col-name"]}>
                        <Link
                          to={`/professional-search/${appointmentsWithInvoices[i].orchid_pro.cognito_sub}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${invoice.provider_business_contact.first_name} ${invoice.provider_business_contact.last_name}`}
                        </Link>
                      </td>
                      <td
                        className={getClasses(
                          "text-right",
                          isInvoiceWrittenOff(invoice) && styles["no-charge"],
                        )}
                      >{`$${invoice.amount_total_invoiced}`}</td>
                      <td
                        className={getClasses(
                          "text-right",
                          isInvoiceWrittenOff(invoice) && styles["no-charge"],
                        )}
                      >{`$${invoice.amount_total_paid}`}</td>
                      <td>
                        {!invoice.is_closed && (
                          <Button
                            variant="link"
                            as={Link as any}
                            to={`/member-dashboard/invoicing/appointment/${invoice.appointment_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Pay invoice
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {/* invoices do not have pagination yet */}
          </div>
        ))}
      {activeTab === "receipt" &&
        (receipts.length === 0 ? (
          <div className={styles["billing-list-empty-state"]}>
            <LazyImage src={RECEIPT_EMPTY_STATE} width={168} height={"auto"} />
            <div className={styles["billing-list-empty-state__title"]}>
              No Receipts.
            </div>
            <div className={styles["billing-list-empty-state__sub-title"]}>
              Your transaction receipt list will appear here.
            </div>
          </div>
        ) : (
          <div
            className={getClasses(
              styles["billing-list"],
              receipts.length > COUNT_PER_PAGE &&
                styles["billing-list--paging"],
            )}
          >
            <Table className={styles["billing-list__table"]}>
              {/* title row */}
              <thead>
                <tr>
                  {/* time */}
                  <th>Date Created</th>
                  {/* session name */}
                  <th>{messages.sessionName}</th>
                  {/* session type */}
                  <th>Type</th>
                  <th>
                    {user?.user_type === "orchid_pro"
                      ? "Patient Name"
                      : "Professional"}
                  </th>
                  <th>Amount Paid</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {receipts
                  .slice(
                    COUNT_PER_PAGE * pageReceipt,
                    COUNT_PER_PAGE * (pageReceipt + 1),
                  )
                  .map((el, i) => (
                    <tr key={`${i}-${el.timeslotId}`}>
                      {/* time */}
                      <td>
                        {el.invoiceTimestamp
                          ? moment
                              .tz(el.invoiceTimestamp, defaultUserTimezone)
                              .format("MM/DD/YYYY hh:mm A z")
                          : "--"}
                      </td>
                      {/* session name */}
                      <td>{el.title}</td>
                      {/* session type */}
                      <td>{TYPE_FILTER_OPTIONS[el.typeId].value}</td>
                      <td className={styles["billing-list__table-col-name"]}>
                        {user?.user_type === "orchid_pro" ? (
                          el.typeId === "ONE_ON_ONE" && (
                            <Link
                              to={`/pro-dashboard/patient-charts/${el.patient?.cognito_sub}/billing`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() =>
                                amplitudePageName &&
                                track(
                                  amplitudePageName,
                                  [AMPLITUDE_COMPONENT_NAME, "patient-link"],
                                  "click",
                                )
                              }
                            >
                              {`${el.patient?.first_name} ${el.patient?.last_name}`}
                            </Link>
                          )
                        ) : (
                          <Link
                            to={`/professional-search/${el.orchidPro.cognito_sub}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              amplitudePageName &&
                              track(
                                amplitudePageName,
                                [AMPLITUDE_COMPONENT_NAME, "pro-link"],
                                "click",
                              )
                            }
                          >
                            {`${el.orchidPro.first_name} ${el.orchidPro.last_name}`}
                          </Link>
                        )}
                      </td>
                      <td>
                        {/* orchid_pro_no_join or cancelled_orchid_pro session won't have receipt anyway, no need to check */}
                        {el.typeId === "ONE_ON_ONE" &&
                          (el.statusFlags.cancelledOrAbandonedByPatient
                            ? `$${(
                                el.cancelRefundOffer?.cancellation_price || 0
                              ).toFixed(2)}`
                            : `$${(
                                el.pricing?.amount_paid_by_patient || 0
                              ).toFixed(2)}`)}
                      </td>
                      {/* <td>{el.status}</td> */}
                      <td>
                        {(() => {
                          const href =
                            el.typeId === "ONE_ON_ONE"
                              ? apiHostAndPath(
                                  `/api/scheduler/v1/invoice/appointment/${el.timeslotId}`,
                                )
                              : el.patients &&
                                el.patients[0]?.payment_status === "completed"
                              ? apiHostAndPath(
                                  `/api/scheduler/v1/invoice/group_series_booking/${el.patients[0].booking_id}`,
                                )
                              : "";

                          return !href ? null : (
                            <ActionsDropdown drop={"start"}>
                              {el.typeId === "ONE_ON_ONE" ? (
                                <Dropdown.Item
                                  href={`/${
                                    user?.user_type === "orchid_pro"
                                      ? "pro"
                                      : "member"
                                  }-dashboard/invoice/appointment/${
                                    el.timeslotId
                                  }/${el.patient?.cognito_sub}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() =>
                                    amplitudePageName &&
                                    track(
                                      amplitudePageName,
                                      [
                                        AMPLITUDE_COMPONENT_NAME,
                                        "view-receipt-dropdown-option",
                                      ],
                                      "click",
                                      {
                                        eventProperties: {
                                          "timeslot-id": el.timeslotId,
                                        },
                                      },
                                    )
                                  }
                                >
                                  {messages.viewReceipt}
                                </Dropdown.Item>
                              ) : (
                                <Dropdown.Item
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() =>
                                    amplitudePageName &&
                                    track(
                                      amplitudePageName,
                                      [
                                        AMPLITUDE_COMPONENT_NAME,
                                        "view-receipt-dropdown-option",
                                      ],
                                      "click",
                                      {
                                        eventProperties: {
                                          "timeslot-id": el.timeslotId,
                                        },
                                      },
                                    )
                                  }
                                >
                                  {messages.viewReceipt}
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                onClick={() => {
                                  amplitudePageName &&
                                    track(
                                      amplitudePageName,
                                      [
                                        AMPLITUDE_COMPONENT_NAME,
                                        "download-receipt-dropdown-option",
                                      ],
                                      "click",
                                      {
                                        eventProperties: {
                                          "timeslot-id": el.timeslotId,
                                        },
                                      },
                                    );
                                  downloadFromDifferentOrigin(
                                    href,
                                    "receipt.pdf",
                                  );
                                }}
                              >
                                {messages.downloadReceipt}
                              </Dropdown.Item>
                            </ActionsDropdown>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>

            {/* pagination */}
            {receipts.length > COUNT_PER_PAGE && (
              <AppPagination
                page={pageReceipt + 1}
                onPageChange={(p) => setPageReceipt(p - 1)}
                dataCount={receipts.length}
                dataCountPerPage={COUNT_PER_PAGE}
                className={getClasses(
                  receipts.length > COUNT_PER_PAGE &&
                    styles["billing-list__pager"],
                )}
              />
            )}
          </div>
        ))}
      {activeTab === "superbill" &&
        (isLoading && superbills.length === 0 ? (
          <div className={styles["billing-list-empty-state"]}>
            <Loading />
          </div>
        ) : superbills.length === 0 ? (
          <div className={styles["billing-list-empty-state"]}>
            <LazyImage
              src={SUPERBILL_EMPTY_STATE}
              width={168}
              height={"auto"}
            />
            <div className={styles["billing-list-empty-state__title"]}>
              No Superbills.
            </div>
            <div className={styles["billing-list-empty-state__sub-title"]}>
              Your Superbill list will appear here.
            </div>
          </div>
        ) : (
          <div
            className={getClasses(
              styles["billing-list"],
              superbills.length > COUNT_PER_PAGE &&
                styles["billing-list--paging"],
            )}
          >
            <Table className={styles["billing-list__table"]}>
              {/* title row */}
              <thead>
                <tr>
                  {/* time */}
                  <th>Date Created</th>
                  {/* session name */}
                  <th>{messages.sessionName}</th>
                  {/* session type */}
                  <th>Type</th>
                  <th>
                    {user?.user_type === "orchid_pro"
                      ? "Patient Name"
                      : "Professional"}
                  </th>
                  <th>Amount Paid</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {superbills
                  .slice(
                    COUNT_PER_PAGE * pageSuperbill,
                    COUNT_PER_PAGE * (pageSuperbill + 1),
                  )
                  .map((el, i) => (
                    <tr key={`${i}-${el.timeslotId}`}>
                      {/* time */}
                      <td>
                        {el.superbills[0].appointment_start
                          ? moment
                              .tz(
                                el.superbills[0].appointment_start,
                                defaultUserTimezone,
                              )
                              .format("MM/DD/YYYY hh:mm A z")
                          : "--"}
                      </td>
                      {/* session name */}
                      <td>{el.title}</td>
                      {/* session type */}
                      <td>{TYPE_FILTER_OPTIONS[el.typeId].value}</td>
                      <td className={styles["billing-list__table-col-name"]}>
                        {user?.user_type === "orchid_pro" ? (
                          el.typeId === "ONE_ON_ONE" && (
                            <Link
                              to={`/pro-dashboard/patient-charts/${el.patient?.cognito_sub}/billing`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() =>
                                amplitudePageName &&
                                track(
                                  amplitudePageName,
                                  [AMPLITUDE_COMPONENT_NAME, "patient-link"],
                                  "click",
                                )
                              }
                            >
                              {`${el.patient?.first_name} ${el.patient?.last_name}`}
                            </Link>
                          )
                        ) : (
                          <Link
                            to={`/professional-search/${el.orchidPro.cognito_sub}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              amplitudePageName &&
                              track(
                                amplitudePageName,
                                [AMPLITUDE_COMPONENT_NAME, "pro-link"],
                                "click",
                              )
                            }
                          >
                            {`${el.orchidPro.first_name} ${el.orchidPro.last_name}`}
                          </Link>
                        )}
                      </td>
                      <td>
                        {/* cancelled sessions won't have superbill anyway, no need to check */}
                        {el.typeId === "ONE_ON_ONE" &&
                          `$${(el.pricing.amount_paid_by_patient || 0).toFixed(
                            2,
                          )}`}
                      </td>
                      <td>
                        <ActionsDropdown drop={"start"}>
                          <Dropdown.Item
                            as={Link as any}
                            to={{
                              pathname: `/${
                                user?.user_type === "orchid_pro"
                                  ? "pro"
                                  : "member"
                              }-dashboard/superbill/appointment/${
                                el.superbills[0]?.statement_no
                              }`,
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              amplitudePageName &&
                                track(
                                  amplitudePageName,
                                  [
                                    AMPLITUDE_COMPONENT_NAME,
                                    "view-superbill-dropdown-option",
                                  ],
                                  "click",
                                  {
                                    eventProperties: {
                                      "timeslot-id": el.timeslotId,
                                    },
                                  },
                                );
                            }}
                          >
                            {messages.viewSuperbill}
                          </Dropdown.Item>
                          {(() => {
                            const href = apiHostAndPath(
                              `/api/notes_secure_data/v1/superbill/appointment/pdf/${el.superbills[0]?.statement_no}-${el.superbills[0]?.last_revision}`,
                            );
                            return !href ? null : (
                              <Dropdown.Item
                                onClick={() => {
                                  amplitudePageName &&
                                    track(
                                      amplitudePageName,
                                      [
                                        AMPLITUDE_COMPONENT_NAME,
                                        "download-superbill-dropdown-option",
                                      ],
                                      "click",
                                      {
                                        eventProperties: {
                                          "timeslot-id": el.timeslotId,
                                        },
                                      },
                                    );
                                  downloadFromDifferentOrigin(
                                    href,
                                    "superbill.pdf",
                                  );
                                }}
                              >
                                {messages.downloadSuperbill}
                              </Dropdown.Item>
                            );
                          })()}
                          {user?.user_type === "orchid_pro" && (
                            <Dropdown.Item
                              onClick={() => {
                                amplitudePageName &&
                                  track(
                                    amplitudePageName,
                                    [
                                      AMPLITUDE_COMPONENT_NAME,
                                      "delete-superbill-dropdown-option",
                                    ],
                                    "click",
                                    {
                                      eventProperties: {
                                        "timeslot-id": el.timeslotId,
                                      },
                                    },
                                  );
                                setStatementNoToDelete(
                                  el.superbills[0]?.statement_no,
                                );
                                setShowConfirmDeleteModal(true);
                              }}
                            >
                              {messages.deleteSuperbill}
                            </Dropdown.Item>
                          )}
                        </ActionsDropdown>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {/* confirm delete superbill */}
            <ConfirmModal
              labelTitle={messages.areYouSure}
              children={messages.theCurrentSuperbillWillNotBeSaved}
              labelConfirm={messages.delete}
              show={showConfirmDeleteModal}
              onCancel={() => setShowConfirmDeleteModal(false)}
              onConfirm={() => onDeleteSuperbill(statementNoToDelete)}
            />

            {/* pagination */}
            {superbills.length > COUNT_PER_PAGE && (
              <AppPagination
                page={pageSuperbill + 1}
                onPageChange={(p) => setPageSuperbill(p - 1)}
                dataCount={superbills.length}
                dataCountPerPage={COUNT_PER_PAGE}
                className={getClasses(
                  superbills.length > COUNT_PER_PAGE &&
                    styles["billing-list__pager"],
                )}
              />
            )}
          </div>
        ))}
    </>
  );
};
export default BillingList;
